import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Filter, X } from 'lucide-react';
import { toast } from 'sonner';

export function LeadFiltersDialog({
  filterOpen,
  setFilterOpen,
  filters,
  setFilters,
  sortBy,
  setSortBy,
  pipelineStages,
  leadSources,
  teamMembers,
  products = [],
}) {
  const activeCount = [filters.status, filters.source, filters.dateFrom, filters.dateTo, filters.assignedTo, filters.icp, filters.product, filters.createdBy].filter(Boolean).length;
  return (
    <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 relative" data-testid="filter-button">
          <Filter className="h-4 w-4" />
          {activeCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-blue-500 text-[9px] text-white flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="filter-dialog">
        <DialogHeader>
          <DialogTitle>Filter & Sort</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Sort By</Label>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="engagement_desc">Engagement: High to Low</SelectItem>
                <SelectItem value="engagement_asc">Engagement: Low to High</SelectItem>
                <SelectItem value="ai_score">AI Score</SelectItem>
                <SelectItem value="value">Deal Value</SelectItem>
                <SelectItem value="name">Name A-Z</SelectItem>
                <SelectItem value="created_desc">Created: Newest first</SelectItem>
                <SelectItem value="created_asc">Created: Oldest first</SelectItem>
                <SelectItem value="modified_desc">Modified: Newest first</SelectItem>
                <SelectItem value="modified_asc">Modified: Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select 
              value={filters.status || undefined} 
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                {pipelineStages.map((status) => (
                  <SelectItem key={status.id} value={status.name}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Source</Label>
            <Select 
              value={filters.source || undefined} 
              onValueChange={(value) => setFilters({ ...filters, source: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                {leadSources.map((source) => (
                  <SelectItem key={source.id} value={source.name}>
                    {source.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Assigned To</Label>
            <Select 
              value={filters.assignedTo || undefined} 
              onValueChange={(value) => setFilters({ ...filters, assignedTo: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Team Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {teamMembers.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>ICP Match</Label>
            <Select 
              value={filters.icp || undefined} 
              onValueChange={(value) => setFilters({ ...filters, icp: value })}
            >
              <SelectTrigger data-testid="icp-filter-select">
                <SelectValue placeholder="All Leads" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High ICP (80%+)</SelectItem>
                <SelectItem value="yes">ICP Match (50%+)</SelectItem>
                <SelectItem value="no">Low/No ICP (&lt;50%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Product / Service</Label>
            <Select
              value={filters.product || undefined}
              onValueChange={(value) => setFilters({ ...filters, product: value })}
            >
              <SelectTrigger data-testid="product-filter-select">
                <SelectValue placeholder="All Products/Services" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Created By</Label>
            <Select
              value={filters.createdBy || undefined}
              onValueChange={(value) => setFilters({ ...filters, createdBy: value })}
            >
              <SelectTrigger data-testid="createdby-filter-select">
                <SelectValue placeholder="Anyone" />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((member) => (
                  <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Created Date From</Label>
            <Input 
              type="date" 
              value={filters.dateFrom} 
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch (_) {} }}
              className="cursor-pointer dark:[color-scheme:dark]"
              data-testid="filter-date-from"
            />
          </div>
          
          <div>
            <Label>Created Date To</Label>
            <Input 
              type="date" 
              value={filters.dateTo} 
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch (_) {} }}
              className="cursor-pointer dark:[color-scheme:dark]"
              data-testid="filter-date-to"
            />
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setFilters({ status: '', source: '', dateFrom: '', dateTo: '', assignedTo: '', icp: '', product: '', createdBy: '' });
                setSortBy('default');
                toast.success('Filters cleared');
              }}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Clear All
            </Button>
            <Button 
              type="button" 
              onClick={() => {
                setFilterOpen(false);
                toast.success('Filters applied');
              }}
              className="flex-1"
            >
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
